import React from 'react';
import PropTypes from 'prop-types';
import { CloseIcon, SegmentedControl } from '../ui';
import { inventoryLink, selectionTitle } from '../../utils/branchView';
import InventoryLink from './InventoryLink';
import ChosenPicker from './ChosenPicker';

export const openLabel = (names, allSelected) => {
    if (names.length === 1) return `Open ${names[0]} inventory`;
    if (allSelected) return 'Open inventory';
    return `Open ${names.length} branches in inventory`;
};

/** "Compare Dickinson with" / "Compare these 2 branches with". */
export const compareLead = (names) => (
    names.length === 1 ? `Compare ${names[0]} with` : `Compare these ${names.length} branches with`
);

/**
 * B1 scope bar: what the page is showing ("North" / "3 branches selected" /
 * "All 13 branches"), the picked branches as removable pills, "n of m",
 * Select all / Clear, and the link that opens the selection in the
 * inventory (several branches allowed, D14). In compare mode (a partial
 * selection) a second, separated line reads "Compare Dickinson with
 * [Whole dealership | Other branches | Median branch | Chosen branches]",
 * each option with a hint (docs/decisions/branch-compare.md). With "Chosen
 * branches", `compare.chosen` adds the "Compare with: …" picker after it.
 */
export default function ScopeBar({
    selectedNames, total, allSelected, onRemove, onSelectAll, onClear, linkBase, compare,
}) {
    const n = selectedNames.length;
    return (
        <section className="br2-scope" role="group" aria-label="Branch selection">
            <span className="v2-eyebrow">Showing</span>
            <span className="br2-who" data-testid="scope-who">{selectionTitle(selectedNames, total)}</span>
            {!allSelected && n > 1 && (
                <span className="v2-pills">
                    {selectedNames.map((name) => (
                        <span key={name} className="v2-pill br2-scope-pill">
                            <span>{name}</span>
                            <button type="button" aria-label={`Remove ${name} from selection`} onClick={() => onRemove(name)}>
                                <CloseIcon />
                            </button>
                        </span>
                    ))}
                </span>
            )}
            <span className="br2-count" data-testid="scope-count"><b>{n}</b> of {total}</span>
            <span className="br2-spacer" />
            {!allSelected && <button type="button" className="v2-clear" onClick={onSelectAll}>Select all</button>}
            {n > 0 && <button type="button" className="v2-clear" onClick={onClear}>Clear</button>}
            {n > 0 && (
                <InventoryLink to={inventoryLink(linkBase)} className="v2-btn v2-btn--primary br2-open">
                    {openLabel(selectedNames, allSelected)} <span aria-hidden="true">›</span>
                </InventoryLink>
            )}
            {compare && (
                <span className="br2-cmpsel" data-testid="scope-compare">
                    <span className="br2-cmpsel-l" aria-hidden="true">{compareLead(selectedNames)}</span>
                    <SegmentedControl label="Compare with" options={compare.options} value={compare.value} onChange={compare.onChange} />
                    {compare.chosen && (
                        <ChosenPicker
                            allNames={compare.chosen.allNames}
                            selectedNames={selectedNames}
                            chosen={compare.chosen.names}
                            onChange={compare.chosen.onChange}
                            open={compare.chosen.open}
                            onOpenChange={compare.chosen.onOpenChange}
                        />
                    )}
                    <span className="br2-cmpsel-h" data-testid="scope-compare-desc">
                        {compare.options.find((o) => o.value === compare.value)?.hint}
                    </span>
                </span>
            )}
        </section>
    );
}

ScopeBar.propTypes = {
    selectedNames: PropTypes.arrayOf(PropTypes.string).isRequired,
    total: PropTypes.number.isRequired,
    allSelected: PropTypes.bool.isRequired,
    onRemove: PropTypes.func.isRequired,
    onSelectAll: PropTypes.func.isRequired,
    onClear: PropTypes.func.isRequired,
    linkBase: PropTypes.shape({ names: PropTypes.array, allNames: PropTypes.array }).isRequired,
    compare: PropTypes.shape({
        value: PropTypes.string.isRequired,
        options: PropTypes.arrayOf(PropTypes.object).isRequired,
        onChange: PropTypes.func.isRequired,
        chosen: PropTypes.shape({
            allNames: PropTypes.arrayOf(PropTypes.string).isRequired,
            names: PropTypes.arrayOf(PropTypes.string).isRequired,
            onChange: PropTypes.func.isRequired,
            open: PropTypes.bool.isRequired,
            onOpenChange: PropTypes.func.isRequired,
        }),
    }),
};
